import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const textareaVariants = cva(
  cn(
    // `dark:bg-input` — matches `inputVariants`/`selectTriggerVariants`. See
    // the note there: `--input` is a raised surface in dark mode, flush with
    // the page in light mode.
    'flex w-full rounded-[var(--radius)] border border-border bg-background dark:bg-input',
    'ring-offset-background',
    // Vertical-only resize for the `autoGrow={false}` escape hatch. Horizontal
    // resize breaks layouts; vertical is the shadcn convention. Auto-growing
    // textareas override this to `resize-none` below — a drag handle on a box
    // that recomputes its own height on the next keystroke is a control that
    // silently undoes itself.
    'resize-y',
    'placeholder:text-muted-foreground',
    // No `shadow-sm`/`shadow-ring` — see `inputVariants`. It is a permanently
    // dead layer (`--shadow-*` is zero-geometry app-wide) and is dropped so a
    // future un-zeroing of the shadow scale doesn't silently grow a colored
    // blur nobody asked for. Border tint + the 1px ring are the real signal.
    'focus-visible:outline-none focus-visible:border-ring/60 focus-visible:ring-1 focus-visible:ring-ring/30',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'transition duration-200 ease-out',
  ),
  {
    variants: {
      size: {
        // `text-base md:text-sm` mirrors `inputVariants`. A textarea under
        // 16px triggers iOS focus-zoom; this one was `text-sm` at every
        // breakpoint, so every mobile focus zoomed the page.
        default: 'min-h-16 px-2.5 py-1.5 text-base md:text-sm',
        sm: 'min-h-12 px-2 py-1 text-base md:text-sm',
        lg: 'min-h-20 px-3 py-2 text-base',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

/**
 * The cap a textarea grows to before it hands back internal scrolling.
 *
 * Ten lines is chosen against the surfaces that actually hold long values:
 * a Concept `Message direction` measured 172px of content (≈8 lines) inside a
 * 62px box, and the longest real Brief field measured 543 characters. Ten
 * covers those without letting one field push a dialog's own actions off
 * screen. Anything that genuinely needs a different ceiling passes `maxRows`.
 */
const DEFAULT_MAX_ROWS = 10;

export interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    VariantProps<typeof textareaVariants> {
  /**
   * Grow to fit the value, up to `maxRows`, then scroll. ON by default: a
   * textarea that hides half of what the user typed behind an invisible
   * internal scroll is never the intent, and the only affordance a fixed box
   * offers is the native OS drag corner, which most people never find.
   *
   * Measured live on `/briefs/:id/concepts/:id`, 2026-08-26: a 62px box held
   * 172px of content — 64% of the value hidden, with nothing on screen saying
   * so. Four surfaces had already hand-rolled this same effect locally
   * (`CaptionDisplay`, `UserMessage`, `MediaStudioCustomPromptInput`,
   * `StudioPanel`); this is that idiom, hoisted, so it stops being re-derived.
   *
   * Pass `false` only when the height is genuinely owned by the layout.
   */
  autoGrow?: boolean;
  /**
   * Lines to grow to before scrolling. Ignored when `autoGrow` is false.
   *
   * A `max-h-*` class on the same element still wins if it is tighter — the
   * measurement below reads the element's own computed `max-height`, so an
   * existing cap can never be overshot into a silently clipped box.
   */
  maxRows?: number;
}

const readPx = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Resize `el` to its content.
 *
 * Three details are load-bearing and were wrong in at least one of the four
 * local copies this replaces:
 *
 * 1. `height = 'auto'` FIRST. Without it `scrollHeight` latches the previous
 *    height and the box never shrinks when text is deleted.
 * 2. Borders are added back. The element is `box-sizing: border-box`, and
 *    `scrollHeight` counts content + padding but NOT border — so assigning
 *    `scrollHeight` straight to `height` leaves the box 2px short forever and
 *    the field scrolls by a sliver at every length.
 * 3. The floor is `max(min-height, rows)`. Call sites express an intended
 *    resting size either way; an explicit `height` overrides the `rows`
 *    attribute, so it has to be re-applied here or every `rows={8}` field
 *    would collapse to its `min-h` on mount.
 */
const resizeToContent = (el: HTMLTextAreaElement, maxRows: number, rows: number | undefined) => {
  const styles = window.getComputedStyle(el);
  const verticalBorder = readPx(styles.borderTopWidth) + readPx(styles.borderBottomWidth);
  const verticalPadding = readPx(styles.paddingTop) + readPx(styles.paddingBottom);

  // jsdom and any browser resolving `line-height: normal` both return a
  // non-numeric string here. Fall back to the font size (≈1.5 in this
  // type system) rather than producing NaN and pinning the field at 0.
  const parsedLineHeight = Number.parseFloat(styles.lineHeight);
  const lineHeight = Number.isFinite(parsedLineHeight)
    ? parsedLineHeight
    : readPx(styles.fontSize) * 1.5;

  el.style.height = 'auto';
  const contentHeight = el.scrollHeight + verticalBorder;

  const chrome = verticalPadding + verticalBorder;
  const cssMaxHeight = readPx(styles.maxHeight);
  const rowsCeiling = lineHeight > 0 ? lineHeight * maxRows + chrome : Number.POSITIVE_INFINITY;
  const ceiling = cssMaxHeight > 0 ? Math.min(rowsCeiling, cssMaxHeight) : rowsCeiling;

  const cssMinHeight = readPx(styles.minHeight);
  const rowsFloor = rows && lineHeight > 0 ? lineHeight * rows + chrome : 0;
  const floor = Math.max(cssMinHeight, rowsFloor);

  const target = Math.min(Math.max(contentHeight, floor), Math.max(ceiling, floor));
  el.style.height = `${target}px`;
  el.style.overflowY = contentHeight > target ? 'auto' : 'hidden';
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      size,
      autoGrow = true,
      maxRows = DEFAULT_MAX_ROWS,
      value,
      defaultValue,
      rows,
      onInput,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    // useLayoutEffect, not useEffect: the resize has to land before paint, or
    // every keystroke paints once at the old height and again at the new one —
    // a visible one-frame jitter while typing.
    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      if (!autoGrow) {
        el.style.height = '';
        el.style.overflowY = '';
        return;
      }
      resizeToContent(el, maxRows, rows);
    }, [autoGrow, maxRows, rows, value, defaultValue]);

    // Width changes re-wrap the text, which changes the height the value needs.
    // A value-only effect leaves the field clipped after a resize, a dock
    // opening, or a rotation, because nothing re-ran. Measured at 500px: a
    // 72-character `Call to action` needed 119px more than it was given.
    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el || !autoGrow || typeof ResizeObserver === 'undefined') return;
      let lastWidth = el.clientWidth;
      const observer = new ResizeObserver(() => {
        if (el.clientWidth === lastWidth) return;
        lastWidth = el.clientWidth;
        resizeToContent(el, maxRows, rows);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [autoGrow, maxRows, rows]);

    // Typed off the prop itself rather than hand-written: React's own
    // `onInput` signature for a textarea is narrower than `FormEvent`, and
    // restating it drifts the moment the React types move.
    const handleInput: React.TextareaHTMLAttributes<HTMLTextAreaElement>['onInput'] = (event) => {
      // Uncontrolled fields, imperative `.value =` writes and IME composition
      // never re-render, so the effect above never fires for them.
      if (autoGrow) resizeToContent(event.currentTarget, maxRows, rows);
      onInput?.(event);
    };

    return (
      <textarea
        className={cn(
          textareaVariants({ size }),
          // A transition that included height would animate it on every
          // keystroke — 200ms of the box lagging behind the caret. Keep this
          // list limited to the focus treatment it was actually written for.
          autoGrow && 'resize-none transition-[border-color,box-shadow,background-color]',
          className,
        )}
        ref={setRef}
        rows={rows}
        value={value}
        defaultValue={defaultValue}
        onInput={handleInput}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
